import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Chip,
  Typography,
  CircularProgress,
  Fade,
  IconButton,
  Tooltip,
  alpha,
  Box,
} from "@mui/material";
import { Edit, TrendingUp, CalendarMonth } from "@mui/icons-material";
import { PenOff } from "lucide-react";
import { formatDate, formatRevenue } from "../../../utils/formatters";

const RevenueTable = ({ revenues, loading, onEdit }) => {
  const adminId = localStorage.getItem("adminId");
  const totalsByCurrency = revenues.reduce((acc, rev) => {
    const curr = rev.currency || "INR";
    acc[curr] = (acc[curr] || 0) + rev.amount;
    return acc;
  }, {});

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
        }}
      >
        <Box sx={{ textAlign: "center", p: 8 }}>
          <CircularProgress size={50} thickness={4} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading revenue data...
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Fade in timeout={700}>
      <Card
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
                }}
              >
                <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                  Company
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                  Date
                </TableCell>

                <TableCell
                  sx={{ fontWeight: 700, color: "text.primary" }}
                  align="right"
                >
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                  Status
                </TableCell>

                <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                  Notes
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.primary" }}>
                  Account Manager
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {revenues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          bgcolor: (theme) =>
                            alpha(theme.palette.grey[400], 0.1),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                        }}
                      >
                        <TrendingUp
                          sx={{ fontSize: 40, color: "text.disabled" }}
                        />
                      </Box>
                      <Typography variant="h6" color="text.secondary">
                        No Revenue Data Found
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.disabled"
                        sx={{ mt: 1 }}
                      >
                        Add your first revenue entry to get started
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                revenues.map((rev) => (
                  <TableRow
                    key={rev._id}
                    hover
                    sx={{
                      "&:hover": {
                        bgcolor: (theme) =>
                          alpha(theme.palette.primary.main, 0.02),
                      },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={rev.user?.companyName || "N/A"}
                        size="small"
                        sx={{ fontWeight: 500 }}
                        color="default"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatDate(rev.paymentDate)}
                        size="small"
                        color="primary"
                        variant="outlined"
                        icon={<CalendarMonth sx={{ fontSize: 16, pr: 1 }} />}
                        sx={{ px: 2, fontWeight: 500 }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primary.main"
                      >
                        {formatRevenue(rev.amount, rev.currency)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={rev.renewalStatus || "1st Payment"}
                        size="small"
                        color={rev.renewalStatus?.includes("Renewal") ? "success" : "info"}
                        variant="outlined"
                        sx={{ fontWeight: 600, fontSize: "0.75rem" }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rev.notes || "-"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {rev.user?.accountManager?.fullName || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer with Grouped Totals */}
        {Object.keys(totalsByCurrency).length > 0 && (
          <Box
            sx={{
              p: 2,
              bgcolor: (theme) => alpha(theme.palette.success.main, 0.05),
              borderTop: "2px solid",
              borderColor: "success.main",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                Total Revenue
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                {Object.entries(totalsByCurrency).map(([curr, amount]) => (
                  <Typography key={curr} variant="h6" fontWeight={700} color="success.main">
                    {formatRevenue(amount, curr)}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Card>
    </Fade>
  );
};

export default RevenueTable;
